import { FC } from 'react'
import { EChartOption } from 'echarts'
import { EChart } from 'src/charts/EChart'
import { campaignColor } from 'src/pages/Campaigns/CampaignComparison/MultiComparison/palette'
import styles from './postVolumeByPlatform.module.scss'
import {
  ContentCampaign,
  PostVolumePlatform,
  VolumeExtreme,
} from '../interfaces'

interface PostVolumeByPlatformProps {
  campaigns: ContentCampaign[]
  highest: VolumeExtreme
  lowest: VolumeExtreme
  platforms: PostVolumePlatform[]
}

const COPY = {
  highest: 'Highest Volume',
  lowest: 'Lowest Volume',
  subtitle:
    'Grouped distribution of published posts across active social networks.',
  title: 'Post Volume by Platform',
}

// Figma draws them at 46; the cap only bites when there are few enough bars
// that ECharts would otherwise stretch them.
const BAR_MAX_WIDTH = 46

const CHART_HEIGHT = 400
const CHART_WIDTH = 1122

const formatCount = (value: number) => value.toLocaleString('en-US')

// One group per platform, one bar per campaign. Grouped rather than stacked:
// these are separate campaigns' posts, not parts of one total, and a stack
// would invent a sum that means nothing.
export const PostVolumeByPlatform: FC<PostVolumeByPlatformProps> = ({
  campaigns,
  highest,
  lowest,
  platforms,
}) => {
  const postsFor = (platform: PostVolumePlatform, campaignId: string) =>
    platform.values.find((value) => value.campaignId === campaignId)?.posts ?? 0

  const options: EChartOption = {
    grid: { bottom: 60, containLabel: true, left: 8, right: 8, top: 16 },
    legend: {
      bottom: 0,
      itemGap: 24,
      itemHeight: 12,
      itemWidth: 12,
      textStyle: {
        color: '#25262d',
        fontFamily: 'Roboto, sans-serif',
        fontSize: 13,
      },
      type: 'plain',
    },
    series: campaigns.map((campaign, index) => ({
      barGap: '20%',
      // Without a ceiling the bars divide whatever the category is given: two
      // analyses on a single platform drew two bars a third of the chart wide.
      barMaxWidth: BAR_MAX_WIDTH,
      data: platforms.map((platform) => postsFor(platform, campaign.id)),
      // Figma caps every bar; the flat foot sits on the axis.
      itemStyle: { barBorderRadius: [8, 8, 0, 0], color: campaignColor(index) },
      name: campaign.name,
      type: 'bar',
    })),
    tooltip: { axisPointer: { type: 'shadow' }, trigger: 'axis' },
    xAxis: {
      axisLabel: {
        color: '#000000b2',
        fontFamily: 'Roboto, sans-serif',
        fontSize: 12,
        margin: 12,
      },
      axisLine: { lineStyle: { color: '#e6ebfa' } },
      axisTick: { show: false },
      data: platforms.map((platform) => platform.name),
      type: 'category',
    },
    yAxis: {
      axisLabel: {
        color: '#000000b2',
        fontFamily: 'Roboto, sans-serif',
        fontSize: 12,
      },
      axisLine: { show: false },
      axisTick: { show: false },
      min: 0,
      splitLine: { lineStyle: { color: '#e6ebfa', type: 'dashed' } },
      type: 'value',
    },
  }

  return (
    <section className={styles.card}>
      <div className={styles.header}>
        <div className={styles.headerText}>
          <h3 className={styles.title}>{COPY.title}</h3>
          <p className={styles.subtitle}>{COPY.subtitle}</p>
        </div>

        {/* Which campaign published most and least. Stated rather than left to
            be totalled off five groups of bars by eye.

            Hidden when nothing was published: naming the same analysis as both
            the busiest and the quietest, both at zero, is not a reading. */}
        {highest.posts > 0 && (
          <div className={styles.extremes}>
            <span className={styles.extreme}>
              <span className={styles.extremeName}>
                {highest.campaignName}:
              </span>
              <span className={styles.extremePill}>
                <b className={styles.high}>
                  {formatCount(highest.posts)} {COPY.highest}
                </b>
              </span>
            </span>

            <span className={styles.extreme}>
              <span className={styles.extremeName}>{lowest.campaignName}:</span>
              <span className={styles.extremePill}>
                <b className={styles.low}>
                  {formatCount(lowest.posts)} {COPY.lowest}
                </b>
              </span>
            </span>
          </div>
        )}
      </div>

      <EChart height={CHART_HEIGHT} options={options} width={CHART_WIDTH} />
    </section>
  )
}
